/*
 * Yandex.Metrica ecommerce pushes.
 *
 * Both regions push Metrica ecommerce (for Yandex Direct). Europe additionally translates the same
 * calls into GA4 ecommerce events (see pushGoogle) sent through the Tag Manager container. Europe
 * pushes in dollars, and only once the visitor has allowed analytics.
 *
 * On why `purchase` is not pushed from the page that starts a payment: everything on the top-up
 * page happens before the player has seen the payment form, so a purchase reported there counts
 * every abandoned cart, declined card and closed tab as revenue. The money is only known to have
 * arrived at the YooKassa webhook.
 *
 * The webhook cannot report it either, though. Metrica's «Электронная коммерция» reports are fed
 * only by the dataLayer event below; the offline-conversion upload the webhook uses carries
 * Target/Price/Currency and has no way to describe an order. So the two halves split:
 *
 *   - the webhook uploads an offline conversion, which fires the goal for every payment, even
 *     when the player never comes back;
 *   - the browser pushes `purchase` on the player's next visit, for payments the backend confirms
 *     and has not already handed out — see /payment/pending-ecommerce and reportPendingPurchases below.
 *
 * Europe has only the second half. Polar's webhook has no offline-conversion upload to call, so a
 * player who pays and never returns is not counted; the normal path is unaffected, because Polar
 * redirects back to /payment.
 */
import { IS_EUROPE, HAS_PAYMENTS, METRIKA_ID } from '../config/region';
import { API_URLS } from '../config/api';
import { apiGet } from './apiUtils';
import { allows, onConsentChange } from './consent';
import { trackEvent } from './analytics';
import { METRIKA_DATA_LAYER } from './yandexMetrika';

// Matches the backend's PAYMENT_CURRENCY for the region.
const CURRENCY = IS_EUROPE ? 'USD' : 'RUB';

const ga4Item = (p) => ({
  item_id: p.id,
  item_name: p.name,
  price: p.price,
  item_category: p.category,
  ...(p.list && { item_list_name: p.list }),
  ...(p.position && { index: p.position }),
  ...(p.quantity && { quantity: p.quantity }),
  ...(p.coupon && { coupon: p.coupon }),
});

const sum = (products) =>
  Number(products.reduce((s, p) => s + p.price * (p.quantity || 1), 0).toFixed(2));

/*
 * The Metrica funnel steps under their GA4 names: impressions → view_item_list, click →
 * select_item, add → begin_checkout (leaving for the payment form), purchase → purchase. `detail`
 * has no counterpart worth sending.
 */
const pushGoogle = (payload) => {
  const send = (name, products, extra) =>
    trackEvent(name, { currency: CURRENCY, items: products.map(ga4Item), ...extra });

  if (payload.impressions) send('view_item_list', payload.impressions);
  if (payload.click) send('select_item', payload.click.products);
  if (payload.add) send('begin_checkout', payload.add.products, { value: sum(payload.add.products) });
  if (payload.purchase) {
    const { actionField, products } = payload.purchase;
    send('purchase', products, { transaction_id: actionField.id, value: actionField.revenue });
  }
};

/* Metrica ecommerce, which Yandex Direct optimises on. Both regions; europe only with analytics
   consent, the same gate its counter loads behind (utils/yandexMetrika.js). */
const pushMetrica = (payload) => {
  if (IS_EUROPE && !allows('analytics')) return;
  window[METRIKA_DATA_LAYER] = window[METRIKA_DATA_LAYER] || [];
  window[METRIKA_DATA_LAYER].push({ ecommerce: { currencyCode: CURRENCY, ...payload } });
};

export const pushEcommerce = (payload) => {
  if (IS_EUROPE) pushGoogle(payload);
  pushMetrica(payload);
};

/* The `pay_click` goal: the player was sent to the payment form. */
export const reportPayClick = () => {
  if (IS_EUROPE) trackEvent('pay_click');
  if (typeof window.ym === 'function') window.ym(METRIKA_ID, 'reachGoal', 'pay_click');
};

/*
 * One confirmed payment, as the backend recorded it.
 *
 * `amount` is what the webhook stored — after discounts, and after whatever the provider actually
 * charged — rather than the price the top-up page had rendered, so the revenue in the report is
 * the real number even when a promo or a subscription discount changed it.
 *
 * `transaction_id` is the provider's payment id, which also carries the unique constraint on the
 * payments table: one order in Metrica is one row in the database.
 */
export const pushPurchase = ({ transaction_id, amount, points_added, subscription_plan, subscription_name }) => {
  // The ids have to be the ones the top-up page pushed for `click` and `add` — `sub_<plan id>`
  // and the star count — or Metrica sees the funnel's steps as unrelated products.
  const product = subscription_name
    ? { id: `sub_${subscription_plan}`, name: `Подписка «${subscription_name}»`, category: 'Подписка' }
    : { id: String(points_added), name: `${points_added} звёзд`, category: 'Звёзды' };

  pushEcommerce({
    purchase: {
      actionField: { id: transaction_id, revenue: amount },
      products: [{ ...product, price: amount, quantity: 1 }],
    },
  });

  // The key event to mark as a conversion for Google Ads: it fires only for a payment the backend
  // has confirmed, with the amount the webhook recorded.
  if (IS_EUROPE) trackEvent('payment_confirmed', { currency: CURRENCY, value: amount, transaction_id });
};

/*
 * Push every payment the backend has confirmed and not yet handed out, then stop.
 *
 * Called once per page load from App, the same way the header asks for the unread news count, so
 * a player who pays and comes back — the normal path, since the provider redirects them back —
 * reports the purchase on the very next page, and one who closes the tab reports it whenever they
 * next open the site, on whatever device they open it on.
 *
 * The backend marks the rows handed out as it answers, so this is at-most-once even if the page
 * is reloaded twice in a row. Nothing here is awaited or shown: a failed request costs an
 * unreported order, never a broken page.
 *
 * Europe waits for analytics consent before asking at all: the backend marks rows handed out as it
 * answers, so asking while the push would be dropped by the consent check would lose the order for
 * good. Returns an unsubscribe function when it has to wait, undefined otherwise.
 */
export const reportPendingPurchases = () => {
  if (!HAS_PAYMENTS) return undefined;
  const fetchPending = () =>
    apiGet(API_URLS.PAYMENT_PENDING_ECOMMERCE, {}, false, '', true)
      .then(res => (res.data?.payments || []).forEach(pushPurchase))
      .catch(() => {});

  if (!IS_EUROPE || allows('analytics')) {
    fetchPending();
    return undefined;
  }
  return onConsentChange((consent) => {
    if (consent.analytics) fetchPending();
  });
};
